import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useAuthenticatedApi } from '../api';
import ReactionButtons from './ReactionButtons';
import { getAuthorName } from '../utils/authorName';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import './Comments.css';

const Comments = ({
  contentId,
  contentType,
  onCommentAdded,
  showCommentsByDefault = false,
  showFormByDefault = false,
  onAddCommentClick,
  onShowCommentsClick,
  countOnly = false,
  // If provided, count-only mode will not fetch until this is true
  deferUntilReady,
}) => {
  const [comments, setComments] = useState([]);
  const [showComments, setShowComments] = useState(showCommentsByDefault);
  const [showForm, setShowForm] = useState(showFormByDefault);
  const [editorContent, setEditorContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyEditorContent, setReplyEditorContent] = useState('');
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);
  const { isAuthenticated } = useAuth0();
  const { authenticatedFetch } = useAuthenticatedApi();
  
  // Initialize Tiptap editor for main comment
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        link: false,
      }),
      Link.configure({
        openOnClick: false,
      }),
      Placeholder.configure({
        placeholder: 'Share your thoughts...',
      }),
    ],
    content: '',
    onUpdate: ({ editor }) => {
      setEditorContent(editor.getText().trim());
    },
  });

  // Initialize Tiptap editor for reply
  const replyEditor = useEditor({
    extensions: [
      StarterKit.configure({
        link: false,
      }),
      Link.configure({
        openOnClick: false,
      }),
      Placeholder.configure({
        placeholder: 'Write a reply...',
      }),
    ],
    content: '',
    onUpdate: ({ editor }) => {
      setReplyEditorContent(editor.getText().trim());
    },
  });

  // Update showComments when showCommentsByDefault prop changes
  useEffect(() => {
    setShowComments(showCommentsByDefault);
  }, [showCommentsByDefault]);

  // Update showForm when showFormByDefault prop changes
  useEffect(() => {
    setShowForm(showFormByDefault);
  }, [showFormByDefault]);

  // Focus editor when form is shown
  useEffect(() => {
    if (showForm && editor) {
      const timer = setTimeout(() => {
        editor.commands.focus();
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [showForm, editor]);

  const fetchComments = useCallback(async () => {
    try {
      setIsLoading(true);
      const url = `${process.env.REACT_APP_API_URL || '/api'}/comments/${contentType}/${contentId}${countOnly ? '/count' : ''}`;

      const response = await authenticatedFetch(url);

      if (response.ok) {
        const data = await response.json();
        if (countOnly) {
          // For count-only mode, create dummy comments for the count
          setComments(Array(data.count || 0).fill({ id: 'dummy', content: '', created_at: '', author_name: '' }));
        } else {
          setComments(data.comments || []);
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to fetch comments:', response.status, errorData);
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setIsLoading(false);
    }
  }, [contentId, contentType, authenticatedFetch, countOnly]);

  // Fetch comments after a delay to let the page load first.
  // For count-only mode, we can optionally defer until the parent indicates main content is ready.
  useEffect(() => {
    if (contentId && contentType) {
      if (countOnly && typeof deferUntilReady === 'boolean' && deferUntilReady === false) {
        return;
      }

      // If no readiness signal is provided, keep a conservative delay for count-only requests
      const delay =
        countOnly && typeof deferUntilReady !== 'boolean'
          ? 3000 // count-only, no readiness signal: wait
          : countOnly
            ? 250 // count-only, ready: tiny delay to yield UI
            : 1000; // full comments: normal delay

      const timer = setTimeout(() => {
        fetchComments();
      }, delay);
      
      return () => clearTimeout(timer);
    }
  }, [contentId, contentType, fetchComments, countOnly, deferUntilReady]);

  const handleSubmitComment = async (e) => {
    e.preventDefault();

    if (!editor || !editorContent || isSubmitting) {
      return;
    }

    try {
      setIsSubmitting(true);

      const htmlContent = editor.getHTML();
      const url = `${process.env.REACT_APP_API_URL || '/api'}/comments/${contentType}/${contentId}`;

      const response = await authenticatedFetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ content: htmlContent }),
      });

      if (response.ok) {
        const data = await response.json();
        setComments(prev => [...prev, data.comment]);
        editor.commands.clearContent();
        setEditorContent('');
        setShowForm(false);
        setShowComments(true);
        if (onCommentAdded) {
          onCommentAdded(data.comment);
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to submit comment:', response.status, errorData);
      }
    } catch (error) {
      console.error('Error submitting comment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitReply = async (e, parentCommentId) => {
    e.preventDefault();

    if (!replyEditor || !replyEditorContent || isSubmittingReply) {
      return;
    }

    try {
      setIsSubmittingReply(true);

      const htmlContent = replyEditor.getHTML();
      const url = `${process.env.REACT_APP_API_URL || '/api'}/comments/${contentType}/${contentId}`;

      const response = await authenticatedFetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          content: htmlContent,
          parent_comment_id: parentCommentId
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setComments(prev => [...prev, data.comment]);
        replyEditor.commands.clearContent();
        setReplyEditorContent('');
        setReplyingTo(null);
        setShowComments(true);
        if (onCommentAdded) {
          onCommentAdded(data.comment);
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to submit reply:', response.status, errorData);
      }
    } catch (error) {
      console.error('Error submitting reply:', error);
    } finally {
      setIsSubmittingReply(false);
    }
  };

  const MenuBar = ({ editor }) => {
    if (!editor) {
      return null;
    }

    return (
      <div style={{
        border: '1px solid #ddd',
        borderBottom: 'none',
        borderRadius: '4px 4px 0 0',
        backgroundColor: '#f8f9fa',
        padding: '0.5rem',
        display: 'flex',
        gap: '0.25rem',
        flexWrap: 'wrap'
      }}>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={!editor.can().chain().focus().toggleBold().run()}
          style={{
            padding: '0.375rem 0.75rem',
            border: '1px solid #ddd',
            borderRadius: '3px',
            backgroundColor: editor.isActive('bold') ? '#38b44a' : 'white',
            color: editor.isActive('bold') ? 'white' : '#333',
            cursor: 'pointer',
            fontWeight: 'bold',
            fontSize: '0.875rem'
          }}
        >
          B
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          disabled={!editor.can().chain().focus().toggleItalic().run()}
          style={{
            padding: '0.375rem 0.75rem',
            border: '1px solid #ddd',
            borderRadius: '3px',
            backgroundColor: editor.isActive('italic') ? '#38b44a' : 'white',
            color: editor.isActive('italic') ? 'white' : '#333',
            cursor: 'pointer',
            fontStyle: 'italic',
            fontSize: '0.875rem'
          }}
        >
          I
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleStrike().run()}
          disabled={!editor.can().chain().focus().toggleStrike().run()}
          style={{
            padding: '0.375rem 0.75rem',
            border: '1px solid #ddd',
            borderRadius: '3px',
            backgroundColor: editor.isActive('strike') ? '#38b44a' : 'white',
            color: editor.isActive('strike') ? 'white' : '#333',
            cursor: 'pointer',
            textDecoration: 'line-through',
            fontSize: '0.875rem'
          }}
        >
          S
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          style={{
            padding: '0.375rem 0.75rem',
            border: '1px solid #ddd',
            borderRadius: '3px',
            backgroundColor: editor.isActive('heading', { level: 2 }) ? '#38b44a' : 'white',
            color: editor.isActive('heading', { level: 2 }) ? 'white' : '#333',
            cursor: 'pointer',
            fontSize: '0.875rem'
          }}
        >
          H2
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          style={{
            padding: '0.375rem 0.75rem',
            border: '1px solid #ddd',
            borderRadius: '3px',
            backgroundColor: editor.isActive('bulletList') ? '#38b44a' : 'white',
            color: editor.isActive('bulletList') ? 'white' : '#333',
            cursor: 'pointer',
            fontSize: '0.875rem'
          }}
        >
          • List
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          style={{
            padding: '0.375rem 0.75rem',
            border: '1px solid #ddd',
            borderRadius: '3px',
            backgroundColor: editor.isActive('orderedList') ? '#38b44a' : 'white',
            color: editor.isActive('orderedList') ? 'white' : '#333',
            cursor: 'pointer',
            fontSize: '0.875rem'
          }}
        >
          1. List
        </button>
      </div>
    );
  };

  const formatRelativeTime = (dateString) => {
    if (!dateString) return 'Unknown';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Invalid date';
      }
      
      const now = new Date();
      const diffInSeconds = Math.floor((now - date) / 1000);
      
      if (diffInSeconds < 60) {
        return 'just now';
      } else if (diffInSeconds < 3600) {
        const minutes = Math.floor(diffInSeconds / 60);
        return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
      } else if (diffInSeconds < 86400) {
        const hours = Math.floor(diffInSeconds / 3600);
        return `${hours} hour${hours === 1 ? '' : 's'} ago`;
      } else if (diffInSeconds < 604800) {
        const days = Math.floor(diffInSeconds / 86400);
        return `${days} day${days === 1 ? '' : 's'} ago`;
      } else if (diffInSeconds < 2592000) {
        const weeks = Math.floor(diffInSeconds / 604800);
        return `${weeks} week${weeks === 1 ? '' : 's'} ago`;
      } else if (diffInSeconds < 31536000) {
        const months = Math.floor(diffInSeconds / 2592000);
        return `${months} month${months === 1 ? '' : 's'} ago`;
      } else {
        const years = Math.floor(diffInSeconds / 31536000);
        return `${years} year${years === 1 ? '' : 's'} ago`;
      }
    } catch (error) {
      return 'Invalid date';
    }
  };

  if (!isAuthenticated) {
    return null;
  }
  
  return (
    <div className="comments-section">
      <div className="comment-header">
        {isLoading ? (
          <div className="comment-loading-spinner">
            <div className="spinner"></div>
            <span>Loading comments...</span>
          </div>
        ) : (
          <>
            {comments.length > 0 && (
              <button
                className="comment-toggle-button"
                onClick={(e) => {
                  e.stopPropagation(); // Prevent parent article onClick from firing
                  if (onShowCommentsClick && !showComments) {
                    // If we have onShowCommentsClick (from blog page), navigate to blog post with comments
                    onShowCommentsClick();
                  } else {
                    // Otherwise, toggle locally (on individual blog post page)
                    setShowComments(!showComments);
                  }
                }}
              >
                {showComments ? 'Hide' : 'Show'} {comments.length} comment{comments.length === 1 ? '' : 's'} {showComments ? '▲' : '▼'}
              </button>
            )}
            
            <button
              onClick={(e) => {
                e.stopPropagation(); // Prevent parent article onClick from firing
                if (onAddCommentClick && !showForm) {
                  onAddCommentClick();
                } else {
                  setShowForm(!showForm);
                }
              }}
              className="add-comment-button"
            >
              {showForm ? 'Cancel' : 'Add Comment'}
            </button>
          </>
        )}
      </div>

      {(showComments || showForm) && (
        <>
          {showForm && (
            <div className="comment-form">
              {!isAuthenticated ? (
                <div style={{ 
                  padding: '1rem', 
                  backgroundColor: '#f8f9fa', 
                  border: '1px solid #dee2e6', 
                  borderRadius: '4px',
                  textAlign: 'center',
                  color: '#6c757d'
                }}>
                  Please log in to add a comment.
                </div>
              ) : (
                editor && (
                  <form onSubmit={handleSubmitComment}>
                    <MenuBar editor={editor} />
                    <div style={{ 
                      border: '1px solid #ddd',
                      borderRadius: '0 0 4px 4px',
                      backgroundColor: 'white',
                      minHeight: '100px',
                      padding: '0.75rem',
                      marginBottom: '0.75rem'
                    }}>
                      <EditorContent editor={editor} />
                    </div>
                    <div className="comment-form-actions">
                      <button 
                        type="button" 
                        onClick={() => {
                          setShowForm(false);
                          editor.commands.clearContent();
                          setEditorContent('');
                        }}
                        className="cancel-button"
                        disabled={isSubmitting}
                      >
                        Cancel
                      </button>
                      <button 
                        type="submit" 
                        className="submit-button"
                        disabled={!editorContent || isSubmitting}
                      >
                        {isSubmitting ? 'Posting...' : 'Post Comment'}
                      </button>
                    </div>
                  </form>
                )
              )}
            </div>
          )}

          {comments.length > 0 && (
            <div className="comments-list">
              {comments.map((comment) => (
                <div key={comment.id} className={`comment-item ${comment.parent_post_id ? 'reply-comment' : ''}`}>
                  <div className="comment-header">
                    <div className="comment-author">
                      <div className="comment-author-avatar">
                        {comment.profile_picture ? (
                          <img 
                            src={`data:${comment.profile_picture_type || 'image/jpeg'};base64,${comment.profile_picture}`}
                            alt={getAuthorName(comment)}
                            className="comment-avatar-image"
                          />
                        ) : (
                          getAuthorName(comment).charAt(0).toUpperCase()
                        )}
                      </div>
                      <div className="comment-author-info">
                        <span className="comment-author-name">
                          {getAuthorName(comment)}
                        </span>
                        <span className="comment-time">
                          {formatRelativeTime(comment.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div 
                    className="comment-content"
                    dangerouslySetInnerHTML={{ __html: comment.content }}
                  />
                  <div className="comment-actions" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <ReactionButtons 
                      contentType="comment" 
                      contentId={comment.id}
                      style={{ fontSize: '0.75rem' }}
                      ownerId={comment.member_id}
                    />
                    <button 
                      onClick={() => setReplyingTo(replyingTo === comment.id ? null : comment.id)}
                      className="reply-button"
                      disabled={!isAuthenticated}
                    >
                      Reply
                    </button>
                  </div>
                  
                  {replyingTo === comment.id && replyEditor && (
                    <div className="reply-form">
                      <form onSubmit={(e) => handleSubmitReply(e, comment.id)}>
                        <MenuBar editor={replyEditor} />
                        <div style={{ 
                          border: '1px solid #ddd',
                          borderRadius: '0 0 4px 4px',
                          backgroundColor: 'white',
                          minHeight: '80px',
                          padding: '0.5rem',
                          marginBottom: '0.5rem'
                        }}>
                          <EditorContent editor={replyEditor} />
                        </div>
                        <div className="reply-form-actions">
                          <button 
                            type="button" 
                            onClick={() => {
                              setReplyingTo(null);
                              replyEditor.commands.clearContent();
                              setReplyEditorContent('');
                            }}
                            className="cancel-reply-button"
                            disabled={isSubmittingReply}
                          >
                            Cancel
                          </button>
                          <button 
                            type="submit" 
                            className="submit-reply-button"
                            disabled={!replyEditorContent || isSubmittingReply}
                          >
                            {isSubmittingReply ? 'Posting...' : 'Post Reply'}
                          </button>
                        </div>
                      </form>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {isLoading && (
            <div className="comment-loading">
              Loading comments...
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Comments;
